import { useTranslation } from 'react-i18next'

import { useUkTimeZone } from '../../../../hooks'
import { stopPropagation } from '../../../../utils/events'
import { useCalendarDayLoadAdvisory } from '../../../CalendarDayLoad'
import { toUserSelectItems } from '../../../User'
import { AttachServiceRequestsModal } from '../../AttachServiceRequestsModal'
import {
  REQUEST_ACTION_ARCHIVE_ITEM,
  REQUEST_ACTION_ASSIGN_ITEM,
  REQUEST_ACTION_CONNECT_DOC_FLOW_ITEM,
  REQUEST_ACTION_CONNECT_SERVICE_REQUEST_ITEM,
  REQUEST_ACTION_CREATE_DOC_FLOW_ITEM,
  REQUEST_ACTION_DUE_DATE_ITEM,
  REQUEST_ACTION_EDIT_CATEGORY_ITEM,
  REQUEST_ACTION_EDIT_LABEL_ITEM,
  REQUEST_ACTION_EDIT_PROVIDER_ITEM,
  REQUEST_ACTION_MARK_AS_PRIVATE,
  REQUEST_ACTION_MARK_AS_PUBLIC,
  REQUEST_ACTION_MARK_AS_READ,
  REQUEST_ACTION_MARK_AS_UNREAD,
  REQUEST_ACTION_RESTORE_ITEM,
  REQUEST_ACTION_UPDATE_STATUS_ITEM,
} from '../../Request.constants.actions'
import {
  getRequestEditStatusActionTypes,
  getRequestItemActionTypes,
} from '../../Request.utils.actions'
import {
  getRequestEditCategoryModalInitialValues,
  RequestEditCategoryModal,
} from '../../RequestEditCategoryModal'
import {
  getRequestEditDueDateWithActionTypesInitialValues,
  getRequestEditDueDateWithActionTypesValidationSchema,
  RequestEditDueDateModal,
  RequestEditDueDateModalFieldsWithActionTypes,
} from '../../RequestEditDueDateModal'
import {
  getRequestEditLabelModalInitialValues,
  RequestEditLabelModal,
} from '../../RequestEditLabelModal'
import {
  getRequestEditManagerModalInitialValues,
  RequestEditManagerModal,
} from '../../RequestEditManagerModal'
import {
  getRequestEditProviderModalInitialValues,
  RequestEditProviderModal,
} from '../../RequestEditProviderModal'
import {
  getRequestEditStatusModalWithActionTypesInitialValues,
  RequestEditStatusModal,
  RequestEditStatusModalFieldsWithActionTypes,
} from '../../RequestEditStatusModal'

export function useRequestTableItemActions({
  getHandleSubmitActionWithType,
  handleAttachRequests,
  handleConnectDocFlowsToItem,
  handleCreateDocFlowForItem,
  massAction,
  showConfirmModal,
  onCloseModal,
  onSetModal,
}) {
  const { t } = useTranslation()

  const { timezone } = useUkTimeZone()

  const { getHandleShowDayLoadAdvisory, handleChangeDayLoadCheck } =
    useCalendarDayLoadAdvisory()

  function setItemAction(action, item) {
    function handleSubmitItemConfirmAction(e) {
      stopPropagation(e)
      massAction({ action, ids: item.id })
    }

    switch (action) {
      case REQUEST_ACTION_ARCHIVE_ITEM:
        showConfirmModal({
          description: t('Modal.RequestArchiveConfirmationTextKey'),
          submitText: t('Modal.RequestArchiveBtnKey'),
          onSubmit: handleSubmitItemConfirmAction,
        })
        break
      case REQUEST_ACTION_ASSIGN_ITEM:
        onSetModal(
          <RequestEditManagerModal
            itemAction
            formFieldsProps={{
              actionTypes: getRequestItemActionTypes(),
            }}
            id={item.id}
            initialValues={getRequestEditManagerModalInitialValues({
              itemAction: true,
              users: toUserSelectItems(item?.assignees),
            })}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action, id: item.id })}
          />
        )
        break
      case REQUEST_ACTION_DUE_DATE_ITEM:
        onSetModal(
          <RequestEditDueDateModal
            itemAction
            Fields={RequestEditDueDateModalFieldsWithActionTypes}
            action={action}
            formFieldsProps={{
              actionTypes: getRequestItemActionTypes(),
              dayLoadProps: {
                dueDate: item.due_date,
                requestIds: [item.id],
                requests: [item],
                onChangeCheck: handleChangeDayLoadCheck,
              },
            }}
            id={item.id}
            initialValues={getRequestEditDueDateWithActionTypesInitialValues(
              item.due_date,
              timezone
            )}
            validationSchema={
              getRequestEditDueDateWithActionTypesValidationSchema
            }
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({
              action,
              getOnSucceed: getHandleShowDayLoadAdvisory,
              id: item.id,
            })}
          />
        )
        break
      case REQUEST_ACTION_EDIT_LABEL_ITEM:
        onSetModal(
          <RequestEditLabelModal
            itemAction
            formFieldsProps={{
              actionTypes: getRequestItemActionTypes(),
            }}
            initialValues={getRequestEditLabelModalInitialValues({
              itemAction: true,
              values: item.labels,
            })}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action, id: item.id })}
          />
        )
        break
      case REQUEST_ACTION_EDIT_CATEGORY_ITEM:
        onSetModal(
          <RequestEditCategoryModal
            itemAction
            formFieldsProps={{
              actionTypes: getRequestItemActionTypes(),
            }}
            initialValues={getRequestEditCategoryModalInitialValues(
              item.category
            )}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action, id: item.id })}
          />
        )
        break
      case REQUEST_ACTION_EDIT_PROVIDER_ITEM:
        onSetModal(
          <RequestEditProviderModal
            itemAction
            formFieldsProps={{
              actionTypes: getRequestItemActionTypes(),
            }}
            id={item.id}
            initialValues={getRequestEditProviderModalInitialValues({
              itemAction: true,
              users: toUserSelectItems(item.contractors),
            })}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action, id: item.id })}
          />
        )
        break
      case REQUEST_ACTION_RESTORE_ITEM:
        showConfirmModal({
          description: t('Modal.RequestRestoreConfirmationTextKey'),
          submitText: t('Modal.RequestRestoreBtnKey'),
          onSubmit: handleSubmitItemConfirmAction,
        })
        break
      case REQUEST_ACTION_MARK_AS_READ:
        massAction({ action, ids: item.id })
        break
      case REQUEST_ACTION_MARK_AS_UNREAD:
        massAction({ action, ids: item.id })
        break
      case REQUEST_ACTION_MARK_AS_PUBLIC:
        massAction({ action, ids: item.id })
        break
      case REQUEST_ACTION_MARK_AS_PRIVATE:
        massAction({ action, ids: item.id })
        break
      case REQUEST_ACTION_UPDATE_STATUS_ITEM:
        onSetModal(
          <RequestEditStatusModal
            Fields={RequestEditStatusModalFieldsWithActionTypes}
            action={action}
            formFieldsProps={{
              actionTypes: getRequestEditStatusActionTypes(),
              typeSelectProps: {
                disabled: true,
              },
            }}
            initialValues={getRequestEditStatusModalWithActionTypesInitialValues(
              item
            )}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action, id: item.id })}
          />
        )
        break

      case REQUEST_ACTION_CONNECT_DOC_FLOW_ITEM:
        handleConnectDocFlowsToItem(item.id)
        break
      case REQUEST_ACTION_CREATE_DOC_FLOW_ITEM:
        handleCreateDocFlowForItem(item)
        break

      case REQUEST_ACTION_CONNECT_SERVICE_REQUEST_ITEM: {
        async function attach(values) {
          return handleAttachRequests({
            ...values,
            action,
            ids: item.id,
          })
        }
        onSetModal(
          <AttachServiceRequestsModal
            multipleSelection
            showLimitNotification
            excludeIds={[item.id, ...item.related_request_ids]}
            onAttach={attach}
            onClose={onCloseModal}
          />
        )
        break
      }
      default:
        return null
    }
  }

  return { setItemAction }
}
